/**
 * useEnergyCertificateConfig — the energy-cert module's low-level ops hook (the
 * ONLY store write path). MODULE-CONTRACT §2.
 *
 * The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|energy_certificate_of_need"]`; this hook wraps
 * it with a typed field-merged read + a single `patch` writer. No imperative
 * handle, no serialize/hydrate — `serializeDraft` / `hydrateFromDraft` already
 * round-trip `productConfigs`.
 *
 * `patch` takes a PARTIAL DTO and merges it over the DEFAULT + the LIVE persisted
 * DTO (read via `getState()`, never a stale snapshot — WAVE2-DECISIONS D-W2-11).
 * It is the user-edit boundary (hydration never calls it) → un-confirms via
 * `markConfigDirty`. The form controller (`useEnergyCertificateForm`) builds the
 * field-granular setters on top of this (C1).
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useFlowConfig } from '../useFlowConfig'
import { ENERGY_CERTIFICATE_DEFAULT, energyCertificateIsComplete, type EnergyCertificateConfigDTO } from './energyCertificateOfNeed.dto'

export interface UseEnergyCertificateConfig {
  /** Field-complete DTO — safe to read every field, even against a partial or
   *  absent persisted entry (§2 partial-DTO invariant). */
  value: EnergyCertificateConfigDTO
  /** Merge-and-write a partial DTO over the LIVE persisted config. Idempotency-
   *  guarded by `setConfig` (JSON-eq); un-confirms the tab. */
  patch: (partial: Partial<EnergyCertificateConfigDTO>) => void
  isComplete: boolean
}

export function useEnergyCertificateConfig(cartItemId: CartItemId): UseEnergyCertificateConfig {
  const { value, patch, isComplete } = useFlowConfig(cartItemId, 'energy_certificate_of_need', ENERGY_CERTIFICATE_DEFAULT, energyCertificateIsComplete)
  return { value, patch, isComplete }
}
